import {
  RICH_TEXT_ATTRS,
  RICH_TEXT_HREF_SCHEMES,
  RICH_TEXT_SRC_SCHEMES,
  RICH_TEXT_TAGS,
} from '@kennisbank/shared';

/**
 * Leeskant-filter voor rich-text HTML, vlak voor dangerouslySetInnerHTML.
 *
 * Nodig naast de serverfilter omdat er al ongefilterde HTML in Firestore staat:
 * e-learning-lessen zijn vóór deze wijziging opgeslagen zoals de beheerder ze
 * intypte. Nieuwe opslag gaat wél door de serverfilter, maar oude documenten
 * worden pas herschreven als iemand ze opnieuw opslaat.
 *
 * Zonder sanitizer-dependency: DOMParser bouwt een inerte tree (geen
 * scriptexecutie, geen resource-loads) die we strippen tot de allowlist uit
 * shared/rich-text.ts en daarna weer serialiseren.
 *
 * Verhuisd uit StudentLessonViewer.tsx, waar dit als lokale helper begon.
 */
const ALLOWED_TAGS = new Set(RICH_TEXT_TAGS);
const ALLOWED_ATTRS: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(RICH_TEXT_ATTRS).map(([tag, attrs]) => [tag, new Set(attrs)]),
);

export function sanitizeRichTextHtml(html: string): string {
  if (typeof window === 'undefined' || !html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const walk = (node: Element): void => {
    for (const child of Array.from(node.children)) {
      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        child.remove();
        continue;
      }
      const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim();
        if (!allowed.has(name)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if (name === 'href' && !RICH_TEXT_HREF_SCHEMES.test(value)) {
          child.removeAttribute(attr.name);
        }
        if (name === 'src' && !RICH_TEXT_SRC_SCHEMES.test(value)) {
          child.removeAttribute(attr.name);
        }
      }
      // Externe links openen veilig (geen window.opener-lek).
      if (tag === 'a' && child.getAttribute('target')?.toLowerCase() === '_blank') {
        child.setAttribute('rel', 'noopener noreferrer');
      }
      walk(child);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
}
